import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Mailbox, MailGroup, OwnedDomain } from "@websede/contracts";
import { Plus, Trash2, Users, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { Button, Card, ErrorNotice, Field, Input, Select } from "./form.js";

/** local-part @ domain, with the first ready domain preselected. */
function AddressInput({
  localPart,
  onLocalPart,
  domains,
  domainId,
  onDomain,
  placeholder,
}: {
  localPart: string;
  onLocalPart: (value: string) => void;
  domains: OwnedDomain[];
  domainId: string;
  onDomain: (id: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <Input
        required
        value={localPart}
        onChange={(e) => onLocalPart(e.target.value.toLowerCase())}
        placeholder={placeholder}
      />
      <span className="text-ink-3">@</span>
      <Select value={domainId} onChange={(e) => onDomain(e.target.value)}>
        {domains.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** Alias chips under a mailbox, with an inline form to add one. */
export function MailboxAliases({ mailbox, domains }: { mailbox: Mailbox; domains: OwnedDomain[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [localPart, setLocalPart] = useState("");
  const [domainId, setDomainId] = useState("");
  const selected = domains.find((d) => d.id === domainId) ?? domains[0];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
  const add = useMutation({
    mutationFn: () =>
      api.mailboxes.addAlias(mailbox.id, { domainId: selected?.id ?? "", localPart }),
    onSuccess: async () => {
      setLocalPart("");
      setAdding(false);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (aliasId: string) => api.mailboxes.removeAlias(mailbox.id, aliasId),
    onSuccess: refresh,
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate();
  };

  return (
    <div className="mt-1 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        {mailbox.aliases.map((a) => (
          <span
            key={a.id}
            className="inline-flex items-center gap-1 rounded-full bg-surface-2 py-0.5 pr-1 pl-2.5 text-xs text-ink-2"
          >
            {a.address}
            <button
              type="button"
              aria-label={t("admin.aliases.remove", { address: a.address })}
              title={t("admin.aliases.remove", { address: a.address })}
              disabled={remove.isPending}
              className="flex size-5 items-center justify-center rounded-full hover:bg-danger-soft hover:text-danger"
              onClick={() => {
                if (window.confirm(t("admin.aliases.confirmRemove", { address: a.address }))) {
                  remove.mutate(a.id);
                }
              }}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        {adding ? null : (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-accent hover:bg-accent-soft/60"
            onClick={() => setAdding(true)}
          >
            <Plus className="size-3" />
            {t("admin.aliases.add")}
          </button>
        )}
      </div>
      {adding ? (
        <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <AddressInput
              localPart={localPart}
              onLocalPart={setLocalPart}
              domains={domains}
              domainId={selected?.id ?? ""}
              onDomain={setDomainId}
              placeholder="info"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={add.isPending || !localPart}>
              {t("admin.aliases.save")}
            </Button>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
      <ErrorNotice error={add.error ?? remove.error} />
    </div>
  );
}

/** Group addresses such as team@: mail sent to one reaches every member mailbox. */
export function MailGroupsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const domains = useQuery({ queryKey: ["domains", "owned"], queryFn: () => api.domains.owned() });
  const mailboxes = useQuery({ queryKey: ["mailboxes"], queryFn: () => api.mailboxes.list() });
  const groups = useQuery({ queryKey: ["mail-groups"], queryFn: () => api.mailGroups.list() });
  const [name, setName] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [domainId, setDomainId] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null);

  const ready = (domains.data?.domains ?? []).filter((d) => d.mailSetupAt);
  const selected = ready.find((d) => d.id === domainId) ?? ready[0];
  const boxes = mailboxes.data?.mailboxes ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
  const create = useMutation({
    mutationFn: () =>
      api.mailGroups.create({ domainId: selected?.id ?? "", localPart, name, memberIds }),
    onSuccess: async () => {
      setName("");
      setLocalPart("");
      setMemberIds([]);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.mailGroups.remove(id),
    onSuccess: refresh,
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  const nameOf = (id: string) => boxes.find((b) => b.id === id)?.displayName ?? "";

  return (
    <Card title={t("admin.groups.title")}>
      <p className="text-sm text-ink-2">{t("admin.groups.body")}</p>
      {ready.length === 0 || boxes.length === 0 ? (
        <p className="text-sm text-ink-3">{t("admin.groups.needMailboxes")}</p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="sm:w-56">
              <Field label={t("admin.groups.name")}>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("admin.groups.namePlaceholder")}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t("admin.mailboxes.address")}>
                <AddressInput
                  localPart={localPart}
                  onLocalPart={setLocalPart}
                  domains={ready}
                  domainId={selected?.id ?? ""}
                  onDomain={setDomainId}
                  placeholder="team"
                />
              </Field>
            </div>
          </div>
          <MemberPicker mailboxes={boxes} value={memberIds} onChange={setMemberIds} />
          <div>
            <Button type="submit" disabled={create.isPending || memberIds.length === 0}>
              {t("admin.groups.create")}
            </Button>
          </div>
        </form>
      )}
      <ErrorNotice error={create.error ?? remove.error ?? groups.error} />
      <ul className="divide-y divide-rule">
        {(groups.data?.groups ?? []).map((g) => (
          <li key={g.id} className="flex flex-col gap-3 py-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
                <Users className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-ink">{g.address}</div>
                <div className="truncate text-xs text-ink-3">
                  {g.name} · {g.memberIds.map(nameOf).filter(Boolean).join(", ") || "—"}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setEditing(editing === g.id ? null : g.id)}
                >
                  {editing === g.id ? t("admin.users.actions.close") : t("admin.groups.edit")}
                </Button>
                <button
                  type="button"
                  aria-label={t("admin.groups.delete")}
                  title={t("admin.groups.delete")}
                  disabled={remove.isPending}
                  className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-danger-soft hover:text-danger"
                  onClick={() => {
                    if (window.confirm(t("admin.groups.confirmDelete", { address: g.address }))) {
                      remove.mutate(g.id);
                    }
                  }}
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
            {editing === g.id ? (
              <GroupEditor group={g} mailboxes={boxes} onDone={() => setEditing(null)} />
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function GroupEditor({
  group,
  mailboxes,
  onDone,
}: {
  group: MailGroup;
  mailboxes: Mailbox[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(group.name);
  const [memberIds, setMemberIds] = useState(group.memberIds);
  const save = useMutation({
    mutationFn: () => api.mailGroups.update(group.id, { name, memberIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
      onDone();
    },
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 rounded-xl bg-surface-2 p-4">
      <div className="sm:w-72">
        <Field label={t("admin.groups.name")}>
          <Input required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      <MemberPicker mailboxes={mailboxes} value={memberIds} onChange={setMemberIds} />
      <ErrorNotice error={save.error} />
      <div>
        <Button type="submit" disabled={save.isPending || memberIds.length === 0}>
          {t("admin.groups.save")}
        </Button>
      </div>
    </form>
  );
}

function MemberPicker({
  mailboxes,
  value,
  onChange,
}: {
  mailboxes: Mailbox[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-xs font-medium text-ink-2">{t("admin.groups.members")}</legend>
      <div className="grid gap-1 sm:grid-cols-2">
        {mailboxes.map((m) => (
          <label
            key={m.id}
            className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
          >
            <input
              type="checkbox"
              className="size-4 cursor-pointer accent-accent"
              checked={value.includes(m.id)}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, m.id] : value.filter((id) => id !== m.id))
              }
            />
            <span className="min-w-0 truncate">
              <span className="text-ink">{m.displayName}</span>{" "}
              <span className="text-xs text-ink-3">{m.address}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
